import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Interfaces para los DTOs
export interface UpdateProfileDto {
  firstName: string;
  lastName: string;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export interface ForgotPasswordDto {
  email: string;
}

export interface VerifyOtpDto {
  email: string;
  otpCode: string;
}

export interface ResetPasswordDto {
  email: string;
  otpCode: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ApiResponse {
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileService {

  private readonly apiUrl = `${environment.apiUrl}/auth`;

  constructor(private http: HttpClient) { }

  // ==================== MÉTODOS DE PERFIL ====================

  /**
   * Actualiza la información del perfil del usuario
   */
  updateProfile(updateProfileDto: UpdateProfileDto): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this.apiUrl}/profile`, updateProfileDto)
      .pipe(catchError(this.handleError));
  }

  /**
   * Cambia la contraseña del usuario
   */
  changePassword(changePasswordDto: ChangePasswordDto): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/change-password`, changePasswordDto)
      .pipe(catchError(this.handleError));
  }

  // ==================== MÉTODOS DE RECUPERACIÓN DE CONTRASEÑA ====================

  /**
   * Solicita un código OTP para recuperación de contraseña
   */
  forgotPassword(forgotPasswordDto: ForgotPasswordDto): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/forgot-password`, forgotPasswordDto)
      .pipe(catchError(this.handleError));
  }

  /**
   * Verifica el código OTP proporcionado
   */
  verifyOtp(verifyOtpDto: VerifyOtpDto): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/verify-otp`, verifyOtpDto)
      .pipe(catchError(this.handleError));
  }

  /**
   * Resetea la contraseña usando el código OTP
   */
  resetPassword(resetPasswordDto: ResetPasswordDto): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/reset-password`, resetPasswordDto)
      .pipe(catchError(this.handleError));
  }

  // ==================== MANEJO DE ERRORES ====================

  /**
   * Maneja errores HTTP de manera consistente
   */
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'Ha ocurrido un error desconocido';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      } else {
        switch (error.status) {
          case 400:
            errorMessage = 'Datos de solicitud inválidos';
            break;
          case 401:
            errorMessage = 'No autorizado';
            break;
          case 403:
            errorMessage = 'Acceso prohibido';
            break;
          case 404:
            errorMessage = 'Recurso no encontrado';
            break;
          case 500:
            errorMessage = 'Error interno del servidor';
            break;
          default:
            errorMessage = `Error del servidor: ${error.status}`;
        }
      }
    }

    console.error('Error en ProfileService:', error);
    return throwError(() => new Error(errorMessage));
  }
}
